import { createFileRoute, useCanGoBack, useRouter } from '@tanstack/react-router';
import { ArrowLeft, PackageSearch } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@nokowazzi/api-client';
import type { LocaleCode, Money } from '@nokowazzi/types';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  DirectionBadge,
  Divider,
  EmptyState,
  IconButton,
  ImageCarousel,
  OfferCard,
  PriceTag,
  Skeleton,
  toast,
  TopAppBar,
  TrustBadge,
} from '@nokowazzi/ui';
import { convertForViewer, useFxRates } from '../lib/fx';
import { useAuthStore } from '../stores/auth';
import { useAcceptOffer } from '../features/orders/api';
import {
  localizedText,
  useRequestDetail,
  useRequestOffers,
  useStartInquiry,
} from '../features/sourcing/api';
import type { OfferDto, RequestDetailDto } from '../features/sourcing/api';
import { categoryLabel, formatRelativeTime, originCurrency } from '../features/sourcing/format';
import { OfferSheet } from '../features/sourcing/OfferSheet';
import { avatarUrl, requestImageUrl } from '../features/sourcing/urls';
import { useViewerCurrency } from '../features/sourcing/viewer';

export const Route = createFileRoute('/sourcing/$requestId')({
  component: RequestDetailScreen,
});

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4 px-5 pt-2">
      <Skeleton className="aspect-square w-full" />
      <Skeleton variant="text" className="w-24" />
      <Skeleton variant="text" className="h-6 w-3/4" />
      <Skeleton variant="text" className="w-32" />
      <Skeleton className="h-20 w-full" />
    </div>
  );
}

function RequestDetailScreen() {
  const { t } = useTranslation();
  const { requestId } = Route.useParams();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = Route.useNavigate();
  const session = useAuthStore((state) => state.session);

  const detailQuery = useRequestDetail(requestId);
  const detail = detailQuery.data;

  const isMine =
    detail !== undefined && session !== null && detail.requester?.id === session.user.id;
  const offersQuery = useRequestOffers(requestId, session !== null && detail !== undefined);

  const [offerSheetOpen, setOfferSheetOpen] = useState(false);

  const handleBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      void navigate({ to: '/sourcing' });
    }
  };

  // 비소유자에게 하단 CTA(소싱/문의) 노출. 마감 요청이어도 문의는 가능하게 하려면 status 조건 완화 가능.
  const showCta = detail !== undefined && !isMine;
  const canOffer = detail !== undefined && detail.status === 'open';
  const startInquiry = useStartInquiry(requestId);

  const requireSession = (): boolean => {
    if (session === null) {
      void navigate({ to: '/auth/signin', search: { redirect: `/sourcing/${requestId}` } });
      return false;
    }
    return true;
  };

  const handleCta = () => {
    if (!requireSession()) return;
    setOfferSheetOpen(true);
  };

  const handleInquiry = () => {
    if (!requireSession()) return;
    startInquiry.mutate(undefined, {
      onSuccess: ({ roomId }) => void navigate({ to: '/chat/$roomId', params: { roomId } }),
      onError: () => toast.show({ type: 'error', message: t('errors.generic') }),
    });
  };

  const isNotFound =
    detailQuery.error instanceof ApiError && detailQuery.error.status === 404;

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <TopAppBar
        leading={
          <IconButton aria-label={t('actions.back')} onClick={handleBack}>
            <ArrowLeft aria-hidden="true" />
          </IconButton>
        }
      />

      {detailQuery.isPending ? (
        <DetailSkeleton />
      ) : detailQuery.isError || detail === undefined ? (
        <EmptyState
          className="mt-16"
          icon={<PackageSearch size={48} strokeWidth={1.5} />}
          title={isNotFound ? t('errors.notFound') : t('errors.generic')}
          action={
            isNotFound ? undefined : (
              <Button variant="tertiary" onClick={() => void detailQuery.refetch()}>
                {t('actions.retry')}
              </Button>
            )
          }
        />
      ) : (
        <DetailBody
          detail={detail}
          offers={offersQuery.data ?? []}
          isMine={isMine}
          showCta={showCta}
          requestId={requestId}
        />
      )}

      {showCta ? (
        <div className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-line bg-page px-5 pt-3 pb-[calc(var(--safe-bottom)+12px)]">
          <Button
            variant="tertiary"
            size="lg"
            className="flex-1"
            loading={startInquiry.isPending}
            onClick={handleInquiry}
          >
            {t('sourcing.inquiry')}
          </Button>
          {canOffer ? (
            <Button variant="primary" size="lg" className="flex-[1.4]" onClick={handleCta}>
              {t('sourcing.sourceCta')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {detail !== undefined ? (
        <OfferSheet
          open={offerSheetOpen}
          onOpenChange={setOfferSheetOpen}
          requestId={requestId}
          currency={originCurrency(detail.direction)}
          onOffered={(roomId) => void navigate({ to: '/chat/$roomId', params: { roomId } })}
        />
      ) : null}
    </div>
  );
}

interface DetailBodyProps {
  detail: RequestDetailDto;
  offers: OfferDto[];
  isMine: boolean;
  showCta: boolean;
  requestId: string;
}

function DetailBody({ detail, offers, isMine, showCta, requestId }: DetailBodyProps) {
  const { t, i18n } = useTranslation();
  const navigate = Route.useNavigate();
  const fxRates = useFxRates();
  const viewerCurrency = useViewerCurrency();

  const acceptOffer = useAcceptOffer();
  const [pendingOfferId, setPendingOfferId] = useState<string | null>(null);

  const locale = i18n.language;
  const viewerLocale: LocaleCode = locale.startsWith('ja') ? 'ja' : 'ko';
  const categoryName =
    detail.category === null ? undefined : categoryLabel(detail.category.name_i18n, locale);

  // 뷰어 언어로 제목/설명 표시 (원문 언어와 다르면 번역, 없으면 원문)
  const displayTitle = localizedText(
    detail.title,
    detail.source_lang,
    detail.translations,
    viewerLocale,
    (tr) => tr.title,
  );
  const displayDescription = localizedText(
    detail.description ?? '',
    detail.source_lang,
    detail.translations,
    viewerLocale,
    (tr) => tr.description,
  );

  const budget: Money = { amount: detail.budget_amount, currency: detail.budget_currency };
  const convertedBudget = convertForViewer(budget, viewerCurrency, fxRates.data);

  const showOffersSection = isMine || offers.length > 0;

  // 요청자이고 모집중일 때만 오퍼 수락 가능
  const canAccept = isMine && detail.status === 'open';

  const handleAccept = () => {
    if (pendingOfferId === null) return;
    acceptOffer.mutate(
      { requestId, offerId: pendingOfferId },
      {
        onSuccess: ({ orderId }) => {
          setPendingOfferId(null);
          void navigate({ to: '/orders/$orderId', params: { orderId } });
        },
        onError: () => {
          setPendingOfferId(null);
          toast.show({ type: 'error', message: t('errors.generic') });
        },
      },
    );
  };

  return (
    <>
    <main
      className={
        showCta
          ? 'flex flex-1 flex-col gap-4 px-5 pt-2 pb-[calc(var(--safe-bottom)+104px)]'
          : 'flex flex-1 flex-col gap-4 px-5 pt-2 pb-[calc(var(--safe-bottom)+24px)]'
      }
    >
      {detail.images.length > 0 ? (
        <ImageCarousel
          images={detail.images.map(requestImageUrl)}
          altPrefix={detail.title}
        />
      ) : null}

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5">
          <DirectionBadge
            direction={detail.direction}
            labels={{ krToJp: t('direction.krToJp'), jpToKr: t('direction.jpToKr') }}
          />
          {categoryName !== undefined ? <Badge tone="neutral">{categoryName}</Badge> : null}
          {detail.status !== 'open' ? (
            <Badge tone="warning">{t(`sourcing.status.${detail.status}`)}</Badge>
          ) : null}
          <span className="ml-auto text-caption2 whitespace-nowrap text-fg-tertiary">
            {formatRelativeTime(detail.created_at, t)}
          </span>
        </div>

        <h1 className="text-title2 text-fg">{displayTitle.text}</h1>
        {displayTitle.translated ? (
          <span className="text-caption2 text-fg-tertiary">{t('content.autoTranslated')}</span>
        ) : null}

        <div className="flex flex-col gap-1">
          <span className="text-caption1 text-fg-secondary">{t('sourcing.budget')}</span>
          <PriceTag
            size="lg"
            money={budget}
            converted={convertedBudget ? { money: convertedBudget } : undefined}
          />
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-caption1 text-fg-secondary">
          <span>
            {t('sourcing.quantity')} <span className="tabular-nums">{detail.quantity}</span>
          </span>
          {detail.city !== null ? (
            <span>
              {t('sourcing.city')} {detail.city}
            </span>
          ) : null}
        </div>
      </div>

      {displayDescription.text !== '' ? (
        <p className="text-body whitespace-pre-wrap break-words text-fg">
          {displayDescription.text}
        </p>
      ) : null}

      <Divider />

      {/* 요청자 — 프로필 화면은 후속 마일스톤이라 클릭 없음 */}
      {detail.requester !== null ? (
        <div className="flex items-center gap-3 rounded-lg border border-line bg-surface p-4">
          <Avatar
            name={detail.requester.display_name}
            src={avatarUrl(detail.requester.avatar_path)}
            size="md"
          />
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-body font-semibold text-fg">
              {detail.requester.display_name}
            </span>
            <TrustBadge
              altitude={detail.requester.trust_score}
              size="sm"
              meterAriaLabel={t('trust.meterLabel')}
            />
          </div>
        </div>
      ) : null}

      {showOffersSection ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-title3 text-fg">
            {isMine
              ? t('sourcing.offers', { count: detail.offer_count })
              : t('my.myOffers')}
          </h2>
          {offers.length === 0 ? (
            <p className="text-caption1 text-fg-secondary">{t('sourcing.noOffers')}</p>
          ) : (
            offers.map((offer) => {
              const price: Money = { amount: offer.price_amount, currency: offer.currency };
              const offerMessage =
                offer.message === null
                  ? undefined
                  : localizedText(
                      offer.message,
                      offer.source_lang,
                      offer.translations,
                      viewerLocale,
                      (tr) => tr.message,
                    ).text;
              return (
                <OfferCard
                  key={offer.id}
                  offer={{
                    id: offer.id,
                    offererName: offer.offerer?.display_name ?? '',
                    offererAvatarUrl: avatarUrl(offer.offerer?.avatar_path ?? null),
                    offererAltitude: offer.offerer?.trust_score ?? 0,
                    price,
                    convertedPrice: convertForViewer(price, viewerCurrency, fxRates.data),
                    etaDaysLabel:
                      offer.eta_days === null
                        ? undefined
                        : t('sourcing.etaDays', { count: offer.eta_days }),
                    message: offerMessage,
                    status: offer.status,
                  }}
                  // 요청자+모집중이면 수락, 그리고 채팅방이 있으면 대화 열기
                  labels={{
                    ...(canAccept ? { accept: t('sourcing.acceptOffer') } : {}),
                    ...(offer.room_id ? { chat: t('escrow.actions.openChat') } : {}),
                  }}
                  onAccept={canAccept ? setPendingOfferId : undefined}
                  onChat={
                    offer.room_id
                      ? () =>
                          void navigate({
                            to: '/chat/$roomId',
                            params: { roomId: offer.room_id as string },
                          })
                      : undefined
                  }
                  meterAriaLabel={t('trust.meterLabel')}
                />
              );
            })
          )}
        </section>
      ) : null}
    </main>

    <Dialog
      open={pendingOfferId !== null}
      onOpenChange={(next) => {
        if (acceptOffer.isPending) return;
        if (!next) setPendingOfferId(null);
      }}
      title={t('sourcing.acceptConfirm.title')}
      description={t('sourcing.acceptConfirm.description')}
      footer={
        <>
          <Button
            variant="tertiary"
            fullWidth
            onClick={() => setPendingOfferId(null)}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            variant="primary"
            fullWidth
            loading={acceptOffer.isPending}
            onClick={handleAccept}
          >
            {t('sourcing.acceptOffer')}
          </Button>
        </>
      }
    />
    </>
  );
}
